import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, FilePlus2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS, ORG_ROLES, roleOf, fmtDate } from "@/lib/roles";
import { StatusBadge, canOrder } from "@/lib/cases";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/app/AppShell";
import { InviteForm } from "@/components/app/InviteForm";

export const OrgTeam = () => {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const load = useCallback(async () => {
    try {
      const [u, i] = await Promise.all([api.get("/org/users"), api.get("/org/invites")]);
      setUsers(u.data);
      setInvites(i.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const revoke = async (id) => {
    try {
      await api.delete(`/org/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mt-8">
      <InviteForm roles={ORG_ROLES} endpoint="/org/invites" onCreated={load} testid="org-invite" />
      {invites.length > 0 && (
        <>
          <h2 className="mt-8 font-display text-lg font-bold text-oath-ink">Pending invites</h2>
          <div className="mt-3 grid gap-2" data-testid="org-invites-list">
            {invites.map((i) => (
              <div key={i.id} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3" data-testid={`org-invite-row-${i.id}`}>
                <div className="text-sm"><span className="font-medium text-oath-ink">{i.email}</span><span className="text-oath-secondary"> · {ROLE_LABELS[i.org_role]}</span>
                  <div className="font-mono text-[11px] text-oath-muted">expires {fmtDate(i.expires_at)}</div></div>
                <button onClick={() => revoke(i.id)} className="rounded-full border border-gray-300 px-3 py-1 text-xs text-oath-ink hover:bg-gray-50" data-testid={`org-invite-revoke-${i.id}`}>Revoke</button>
              </div>
            ))}
          </div>
        </>
      )}
      <h2 className="mt-8 font-display text-lg font-bold text-oath-ink">Your team</h2>
      <div className="mt-3 grid gap-2" data-testid="org-team-list">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3" data-testid={`org-user-${u.id}`}>
            <div><div className="font-medium text-oath-ink">{u.name}</div><div className="text-xs text-oath-secondary">{u.email}</div></div>
            <div className="text-right text-xs text-oath-secondary">{ROLE_LABELS[u.org_role]}<div className="font-mono text-[11px] text-oath-muted">{fmtDate(u.last_login_at)}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StaffWelcome = ({ user }) => (
  <div className="rounded-3xl border border-gray-200 bg-white p-6" data-testid="console-welcome">
    <div className="flex items-start gap-4">
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-oath-accent-light text-oath-accent"><ShieldCheck className="h-5 w-5" /></span>
      <div>
        <div className="font-mono-label text-[10px] font-semibold text-oath-muted">SIGNED IN AS</div>
        <div className="mt-1 font-display text-xl font-bold text-oath-ink" data-testid="console-role">{ROLE_LABELS[roleOf(user)]}</div>
        <p className="mt-2 max-w-xl text-sm text-oath-secondary">Your internal workspace — triangulation, field dispatch and QA — opens in the next releases.</p>
      </div>
    </div>
  </div>
);

const OrgOverview = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/cases/stats"), api.get("/cases")])
      .then(([s, c]) => {
        setStats(s.data);
        setRecent(c.data.slice(0, 6));
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const by = stats?.by_status || {};
  const pending = user.org_status && user.org_status !== "active";
  return (
    <>
      {pending && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800" data-testid="org-pending-banner">
          <b>Your organization is {user.org_status === "declined" ? "not approved" : "waiting for approval"}.</b> {user.org_status === "declined" ? "Contact us if you think this is a mistake." : "Our team reviews every business before it can order verifications. We'll email you once you're approved."}
        </div>
      )}
      <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 text-sm" data-testid="org-credits">
        <span className="font-mono-label text-[9px] font-semibold text-oath-muted">VERIFICATION CREDITS</span>
        <span className="ml-3 font-display text-xl font-bold text-oath-ink">{user.org_credits ?? 0}</span>
        <Link to="/pricing" className="ml-3 text-oath-accent underline">Buy more</Link>
      </div>
      <div className="rounded-3xl border border-gray-200 bg-white p-6" data-testid="console-welcome">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-mono-label text-[10px] font-semibold text-oath-muted">SIGNED IN AS</div>
            <div className="mt-1 font-display text-xl font-bold text-oath-ink" data-testid="console-role">{ROLE_LABELS[roleOf(user)]}</div>
          </div>
          {canOrder(user) && !pending && (
            <Link to="/console/cases/new" data-testid="console-new-case-button"
              className="inline-flex items-center gap-2 rounded-full bg-oath-ink px-5 py-3 font-mono-label text-[11px] font-semibold text-white hover:bg-black">
              <FilePlus2 className="h-4 w-4" /> ORDER A VERIFICATION
            </Link>
          )}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[["total", "ALL CASES", stats?.total], ["awaiting", "AWAITING CONSENT", (by.awaiting_consent || 0) + (by.awaiting_consent_upload || 0)], ["given", "IN PROGRESS", (by.consent_given || 0) + (by.in_progress || 0) + (by.qa_review || 0)],
          ["delivered", "DELIVERED", by.delivered]].map(([id, label, v]) => (
          <div key={id} className="rounded-2xl border border-gray-200 bg-white p-4" data-testid={`case-stat-${id}`}>
            <div className="font-mono-label text-[9px] font-semibold text-oath-muted">{label}</div>
            <div className="mt-2 font-display text-3xl font-bold text-oath-ink">{stats ? v || 0 : "—"}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-oath-ink">Recent cases</h2>
        <Link to="/console/cases" className="inline-flex items-center gap-1 text-sm text-oath-accent" data-testid="console-all-cases-link">All cases <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-3 grid gap-2" data-testid="console-recent-cases">
        {recent.length === 0 && <div className="rounded-2xl border border-dashed border-gray-300 bg-white/60 p-8 text-center text-sm text-oath-muted">No cases yet.</div>}
        {recent.map((c) => (
          <Link key={c.id} to={`/console/cases/${c.id}`} data-testid={`recent-case-${c.id}`}
            className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-gray-300">
            <div><span className="font-mono text-xs text-oath-muted">{c.case_number}</span>
              <div className="font-medium text-oath-ink">{c.subject.first_name} {c.subject.last_name}</div></div>
            <div className="flex items-center gap-3"><span className="font-mono text-[11px] text-oath-muted">{fmtDate(c.created_at)}</span><StatusBadge status={c.status} testid={`recent-case-status-${c.id}`} /></div>
          </Link>
        ))}
      </div>
    </>
  );
};

export default function Console() {
  const { user } = useAuth();
  return (
    <AppShell eyebrow={user.org_name ? `— ${user.org_name.toUpperCase()}` : "— RESIDENCEPROOF STAFF"} title={`Welcome, ${user.name?.split(" ")[0] || ""}`}>
      {user.org_id ? <OrgOverview user={user} /> : <StaffWelcome user={user} />}
    </AppShell>
  );
}

